import React, { useState } from 'react';
import { useQueryClient, useMutation, useIsFetching } from '@tanstack/react-query';
import { ClipboardList, CheckCircle, User, Plus, Edit2, Trash2, X, Check, Loader2 } from 'lucide-react';
import { Badge } from '../../../../components/ui/Badge';
import { workOrdersService } from '../../services/workOrdersService';

export const WorkOrdersTasks = ({ workOrder, dateFormat }) => {
  const queryClient = useQueryClient();
  const workOrderId = workOrder?.id;
  //Show tasks by task_number
  const tasks = [...(workOrder?.tasks || [])].sort((a, b) => a.task_number - b.task_number);

  const [isAdding, setIsAdding] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [newTaskDesc, setNewTaskDesc] = useState('');
  const [newTaskAssignee, setNewTaskAssignee] = useState('');
  const [editTaskDesc, setEditTaskDesc] = useState('');
  const [editTaskAssignee, setEditTaskAssignee] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const invalidateWorkOrders = () => {
    queryClient.invalidateQueries({ queryKey: ['wo'] });
  };

  const nextTaskNumber = tasks.length + 1;

  //Mutations
  const addTaskMutation = useMutation({
    mutationFn: (data) => workOrdersService.addTask(workOrderId, data),
    onSuccess: () => {
      invalidateWorkOrders();
      setIsAdding(false);
      setNewTaskDesc('');
      setNewTaskAssignee('');
      setIsSubmitting(false);
    },
    onError: (err) => {
      console.error('Failed to add task:', err);
      setIsSubmitting(false);
    },
  });

  const updateTaskMutation = useMutation({
    mutationFn: ({ taskId, data }) => workOrdersService.updateTask(taskId, data),
    onSuccess: () => {
      invalidateWorkOrders();
      setEditingTaskId(null);
    },
    onError: (err) => {
      console.error('Failed to update task:', err);
    },

  });

  const deleteTaskMutation = useMutation({
    mutationFn: (taskId) => workOrdersService.deleteTask(taskId),
    onSuccess: () => invalidateWorkOrders(),
  });

  //Handles
  const handleAddTask = () => {
    if (!newTaskDesc.trim()) return;
    addTaskMutation.mutate({
      task_number: nextTaskNumber,
      description: newTaskDesc.trim(),
      assigned_to: newTaskAssignee ? parseInt(newTaskAssignee.split(',')[0]) : null,
      is_external: newTaskAssignee ? newTaskAssignee.split(',')[1] === 'true' : false,
      status: 'pending',
    });
  };

  const handleAddFormSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    handleAddTask();
  };
  console.log(editTaskAssignee, 'Edit')
  console.log(newTaskAssignee, 'New')
  const handleUpdateTask = (taskId, updatedFields) => {
    const backendData = {};
    if (updatedFields.description !== undefined) backendData.description = updatedFields.description;
    if (updatedFields.assignee_id !== undefined) backendData.assigned_to = parseInt(updatedFields.assignee_id) || null;
    if (updatedFields.status !== undefined) backendData.status = updatedFields.status;
    if (updatedFields.is_external !== undefined) backendData.is_external = updatedFields.is_external;
    updateTaskMutation.mutate({ taskId, data: backendData });
  };

  const handleEditFormSubmit = (e, taskId) => {
    e.preventDefault();
    handleUpdateTask(taskId, {
      description: editTaskDesc,
      assignee_id: editTaskAssignee ? editTaskAssignee.split(',')[0] : null,
      is_external: editTaskAssignee ? editTaskAssignee.split(',')[1] === 'true' : false,
    });
    setEditingTaskId(null);
  };

  const handleDeleteTask = (taskId) => {
    if (window.confirm('Delete this task?')) {
      deleteTaskMutation.mutate(taskId);
    }
  };

  if (!workOrderId) return null;

  //Consult internal and externals assignments
  const assigneeOptions = workOrder?.assignments || [];
  const externalAssigneeOptions = workOrder?.assignments_external || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-gray-400" />
          Tasks
          <Badge className="bg-gray-700 text-gray-300">
            {tasks.length} {tasks.length === 1 ? 'task' : 'tasks'}
          </Badge>
        </h3>

        <button
          onClick={() => setIsAdding(true)}
          className="inline-flex items-center gap-1 px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 rounded-lg text-white"
        >
          <Plus className="w-4 h-4" /> Add Task
        </button>
      </div>

      {/* Add form */}
      {isAdding && (
        <form onSubmit={handleAddFormSubmit} className="bg-gray-700/50 rounded-lg p-4 border border-gray-600">
          <div className="flex flex-col gap-2">
            <input
              type="text"
              value={newTaskDesc}
              onChange={(e) => setNewTaskDesc(e.target.value)}
              placeholder="Task description"
              className="px-3 py-2 rounded bg-gray-800 text-white border border-gray-600"
              autoFocus
            />
            <select
              value={newTaskAssignee}
              onChange={(e) => setNewTaskAssignee(e.target.value)}
              className="px-3 py-2 rounded bg-gray-800 text-white border border-gray-600"
            >
              <option value="">Unassigned</option>
              {assigneeOptions.map((assignment) => (
                <option key={`internal-${assignment.employee_id}`} value={[assignment.employee_id, false]}>
                  {`${assignment.employee_name} - ${assignment.role}`}
                </option>
              ))}
              {externalAssigneeOptions.map((extAssignment) => (
                <option key={`external-${extAssignment.employee_id}`} value={[extAssignment.employee_id, true]}>
                  {`${extAssignment.employee_name} - ${extAssignment.role}`}
                </option>
              ))}
            </select>
            <div className="flex justify-end gap-2 mt-2">
              <button
                type="button"
                onClick={() => setIsAdding(false)}
                className="px-3 py-1 text-gray-400 hover:text-white"
              >
                Cancel
              </button>
              <button type="submit" className="px-3 py-1 bg-blue-600 rounded-lg hover:bg-blue-700  disabled:opacity-50 disabled:cursor-not-allowed text-white inline-flex items-center gap-2"
                disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Add
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Task list */}
      <div className="space-y-3">
        {tasks.map((task) => (
          <div key={task.id} className="bg-gray-700/30 rounded-lg border border-gray-600 p-4">
            {editingTaskId === task.id ? (
              // Edit form
              <form onSubmit={(e) => handleEditFormSubmit(e, task.id)} className="flex flex-col gap-2">
                <textarea
                  value={editTaskDesc}
                  onChange={(e) => setEditTaskDesc(e.target.value)}
                  className="px-3 py-2 rounded bg-gray-800 text-white border border-gray-600 overflow-y-auto"
                  rows={4}
                  autoFocus
                />
                <select
                  value={editTaskAssignee}
                  onChange={(e) => setEditTaskAssignee(e.target.value)}
                  className="px-3 py-2 rounded bg-gray-800 text-white border border-gray-600"
                >
                  <option value="">Unassigned</option>
                  {assigneeOptions.map((assignment) => (
                    <option key={`internal-${assignment.employee_id}`} value={[assignment.employee_id, false]}>
                      {`${assignment.employee_name} - ${assignment.role}`}
                    </option>
                  ))}
                  {externalAssigneeOptions.map((extAssignment) => (
                    <option key={`external-${extAssignment.employee_id}`} value={[extAssignment.employee_id, true]}>
                      {`${extAssignment.employee_name} - ${extAssignment.role}`}
                    </option>
                  ))}
                </select>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setEditingTaskId(null)}
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <button type="submit" className="text-green-400 hover:text-green-300">
                    <Check className="w-4 h-4" />
                  </button>
                </div>
              </form>
            ) : (
              // Read mode
              <>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-400 font-mono">#{task.task_number}</span>
                    <p className="text-white font-medium">{task.description}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      className={`${task.status === 'completed'
                        ? 'bg-green-900/50 text-green-200'
                        : task.status === 'in_progress'
                          ? 'bg-yellow-900/50 text-yellow-200'
                          : 'bg-gray-700/50 text-gray-400'
                        }`}
                    >
                      {task.status ? task.status.charAt(0).toUpperCase() + task.status.slice(1) : 'Pending'}
                    </Badge>
                    <button
                      onClick={() => {
                        setEditingTaskId(task.id);
                        setEditTaskDesc(task.description);
                        setEditTaskAssignee(task.assigned_to ? `${task.assigned_to},${task.is_external}` : '');
                      }}
                      className="text-gray-400 hover:text-blue-400"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDeleteTask(task.id)} className="text-gray-400 hover:text-red-400">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {task.assigned_to && (
                  <div className="flex items-center gap-2 mt-2 text-sm text-gray-400">
                    <User className="w-3 h-3" />
                    <span>Assigned to: {task.assignee_name || task.assigned_to}</span>
                  </div>
                )}
                {task.completed_at && (
                  <div className="flex items-center gap-2 mt-2 text-sm text-green-400">
                    <CheckCircle className="w-3 h-3" />
                    <span>Completed: {dateFormat(task.completed_at)}</span>
                  </div>
                )}
              </>
            )}
          </div>
        ))}
        {tasks.length === 0 && !isAdding && (
          <div className="text-center py-8 text-gray-400">
            <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No tasks yet. Click "Add Task" to create one.</p>
          </div>
        )}
      </div>
    </div>
  );
};